import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type ReactElement } from 'react';
import Skeleton from 'react-loading-skeleton';
import styles from './score-gauge.module.scss';
type ScoreGaugeLayoutProps = {
  ariaLabel?: string;
  isActive?: boolean;
  isSecondary?: boolean;
  isSlim?: boolean;
  labelSlot: ReactElement | string;
  scoreGaugeSlot: ReactElement;
  scoreSlot?: ReactElement;
};
const ScoreGaugeLayout = t0 => {
  const $ = _c(14);
  const {
    ariaLabel,
    isActive,
    isSecondary,
    isSlim,
    labelSlot,
    scoreGaugeSlot,
    scoreSlot
  } = t0;
  let t1;
  if ($[0] !== isActive || $[1] !== isSecondary || $[2] !== isSlim) {
    t1 = classNames(styles["score-gauge"], {
      [styles["score-gauge--active"]]: isActive,
      [styles["score-gauge--secondary"]]: isSecondary,
      [styles["score-gauge--slim"]]: isSlim
    });
    $[0] = isActive;
    $[1] = isSecondary;
    $[2] = isSlim;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = scoreSlot !== undefined && scoreSlot;
  let t3;
  if ($[4] !== scoreGaugeSlot || $[5] !== t2) {
    t3 = <div aria-hidden="true" className={styles["score-gauge__indicator-container"]}>{scoreGaugeSlot}{t2}</div>;
    $[4] = scoreGaugeSlot;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== labelSlot) {
    t4 = <div className={styles["score-gauge__label"]} data-testid="score-gauge-text">{labelSlot}</div>;
    $[7] = labelSlot;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== ariaLabel || $[10] !== t1 || $[11] !== t3 || $[12] !== t4) {
    t5 = <div aria-label={ariaLabel} className={t1} data-testid="score-gauge-wrapper">{t3}{t4}</div>;
    $[9] = ariaLabel;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
type ScoreGaugeProps = {
  ariaLabel?: string;
  isActive?: boolean;
  isSecondary?: boolean;
  isSlim?: boolean;
  score?: number;
  scoreLabel?: null | string;
};
export const ScoreGauge = ({
  ariaLabel,
  isActive,
  isSecondary,
  isSlim,
  score,
  scoreLabel
}: ScoreGaugeProps) => {
  if (score === undefined || scoreLabel === undefined) {
    return <ScoreGaugeLayout isSecondary={isSecondary} labelSlot={<Skeleton width={80} />} scoreGaugeSlot={<Skeleton circle height={isSecondary ? 56 : 96} width={isSecondary ? 56 : 96} />} />;
  }
  const scorePercent = score * 10;
  const radius = 48;
  const strokeWidth = isSlim ? 4 : 6;
  const innerRadius = radius - strokeWidth;
  const circumference = innerRadius * 2 * Math.PI;
  const arc = circumference * 0.75;
  const dashArray = `${arc} ${circumference}`;
  const transform = `rotate(135, ${radius}, ${radius})`;
  const offset = arc - scorePercent / 100 * arc;
  const FILL_PARENT_CLASSNAME = 'score-gauge__indicator-fill';
  let fillClassModifier;
  switch (true) {
    case isSecondary && !isActive:
      fillClassModifier = 'secondary';
      break;
    case score >= 1 && score <= 3:
      fillClassModifier = 'range-one';
      break;
    case score >= 4 && score <= 7:
      fillClassModifier = 'range-two';
      break;
    default:
      fillClassModifier = 'range-three';
  }
  const fillClasses = classNames(styles[FILL_PARENT_CLASSNAME], styles[`${FILL_PARENT_CLASSNAME}--${fillClassModifier}`]);
  return <ScoreGaugeLayout ariaLabel={ariaLabel} isActive={isActive} isSecondary={isSecondary} isSlim={isSlim} labelSlot={scoreLabel ?? ''} scoreGaugeSlot={<svg viewBox="0 0 96 96">
          <defs />
          <circle className={styles['score-gauge__indicator-base']} cx={radius} cy={radius} data-testid="score-gauge-base" r={innerRadius} strokeDasharray={dashArray} strokeWidth={strokeWidth} transform={transform} />
          <circle className={fillClasses} cx={radius} cy={radius} data-testid="score-gauge-fill" r={innerRadius} strokeDasharray={dashArray} strokeDashoffset={offset} strokeWidth={strokeWidth} transform={transform} />
        </svg>} scoreSlot={<span className={styles['score-gauge__score']} data-testid="score-gauge-number">
          {score}
        </span>} />;
};